import { useMemo } from 'react';
import type { VideoRoomState } from '../providers';

export interface NetworkQuality {
  /** Local participant network quality level (0-5, 0 = unknown) */
  localLevel: number;
  /** Remote participant network quality level (0-5, 0 = unknown) */
  remoteLevel: number;
  /** True when localLevel > 0 AND localLevel < 3 (poor/fair quality) */
  isLow: boolean;
}

/**
 * Derives network quality state from video provider state.
 *
 * Returns `isLow` boolean for ConsultationScreen to use with a debounced
 * effect (30 second debounce) to fire toast notifications when quality drops.
 * This hook does NOT fire toasts itself -- it only exposes state.
 *
 * @param videoState - Current video room state from useLiveVideo
 */
export function useNetworkQuality(videoState: Pick<VideoRoomState, 'localNetworkQuality' | 'remoteNetworkQuality'>): NetworkQuality {
  const localLevel = videoState.localNetworkQuality;
  const remoteLevel = videoState.remoteNetworkQuality;

  const isLow = useMemo(() => {
    return localLevel > 0 && localLevel < 3;
  }, [localLevel]);

  return {
    localLevel,
    remoteLevel,
    isLow,
  };
}
